import { formatPrice, type Locale } from "@nh/shared";
import { useTranslation } from "react-i18next";
import { numberLocaleOf, type ResolvedPrice } from "../shop.catalog";
import { useImageFallback } from "../useImageFallback";

export interface ProductHeroProps {
  /** La imagen ACTIVA de la galería. `undefined` = el producto no tiene foto. */
  image: string | undefined;
  name: string;
  category: string | undefined;
  tagline: string | undefined;
  /** `editorial.badgeLabel`. Sin él NO hay etiqueta: no cae a la categoría. */
  badgeLabel: string | undefined;
  price: ResolvedPrice | null;
  locale: Locale;
}

/**
 * El fondo del estandarte cuando SÍ hay foto.
 *
 * Es literal del original: `mist → cream-2 → violeta al 45%`. Bajo una
 * foto a sangre no se ve nunca —`object-cover` la tapa entera— y aun así
 * está, porque es lo que se ve durante los milisegundos en que la imagen
 * todavía no ha pintado, y un destello blanco ahí da un parpadeo.
 */
const HERO_BACKDROP =
  "linear-gradient(180deg, var(--mist) 0%, var(--cream-2) 55%, rgba(92,79,214,0.45) 100%)";

/**
 * El velo del héroe, y por qué sus alfas NO son las del original.
 *
 * ── La geometría sí es la del original ───────────────────────────────
 * Mismo sentido (`to top`), mismo color en todas las paradas
 * (`violet-pop` #5C4FD6) y los mismos CUATRO cortes: 0%, 30%, 55% y 80%.
 * Ahí se acaba el degradado y la foto queda limpia, igual que allá.
 *
 * ── Lo que se subió, y por qué ───────────────────────────────────────
 * El original va `0.78 / 0.42 / 0.12 / 0`. Sobre una foto CLARA —y las
 * del catálogo son botes sobre fondo casi blanco— eso no llega a 4.5:1:
 *
 *   parada 0%  (0.78) sobre blanco → rgb(128,118,223) → 3.76:1
 *   parada 30% (0.42) sobre blanco → rgb(186,181,238) → 2.02:1
 *
 * Y el texto no vive en el 0%: el bloque inferior tiene 56 px de aire por
 * abajo, así que el `<h1>` cae hacia el 7% y el rótulo hacia el 22% — o
 * sea, en la franja que peor está. El encargo dice que en ese caso hay
 * que subirlo y decirlo, así que se sube.
 *
 * Las alfas quedan en `0.93 / 0.80 / 0.34 / 0`, y las dos paradas bajas
 * viran hacia `ink-2` (#2E2540) para que el violeta no se vuelva neón al
 * subir la opacidad — es el mismo camino que hace el degradado de marca
 * `ds-grad-hero-card`. Peor caso teórico, sobre blanco puro:
 *
 *   parada 0%  (0.93) → rgb(60,52,77)    → 12.9:1
 *   parada 30% (0.80) → rgb(102,94,147)  →  5.9:1
 *
 * Las cifras medidas sobre las capturas reales van en el informe.
 *
 * ── Por qué no es `bg-gradient-to-t from-… via-… to-…` ───────────────
 * Tailwind reparte sus tres paradas en 0/50/100 y no deja mover los
 * cortes. Aquí el corte importa: el velo tiene que estar plano y opaco
 * hasta donde llega el texto y desvanecerse DESPUÉS.
 */
const HERO_VEIL =
  "linear-gradient(to top," +
  "rgba(46,37,64,0.93) 0%," +
  "rgba(64,54,120,0.80) 30%," +
  "rgba(92,79,214,0.34) 55%," +
  "rgba(92,79,214,0) 80%)";

/** El descuento que se anuncia bajo el precio. `0` = no hay rebaja. */
export function discountPercent(price: ResolvedPrice | null): number {
  if (!price?.compareAtCents) return 0;
  return Math.round(((price.compareAtCents - price.cents) / price.compareAtCents) * 100);
}

/**
 * El estandarte de la ficha: la foto A SANGRE, el velo morado, la etiqueta,
 * el nombre y el precio.
 *
 * ── A sangre, y con las dos esquinas de abajo redondeadas ────────────
 * Es la diferencia que más se notaba: aquí la foto estaba METIDA en una
 * tarjeta con márgenes, y en el original ocupa el ancho entero de la
 * ventana —`inset-0`, `object-cover`— con 78vh de alto y 48 px de radio
 * SOLO abajo. Por eso este bloque se pinta fuera del contenedor de la
 * ficha, y no dentro.
 *
 * ── Las medidas, literales del original ──────────────────────────────
 *   sección   min-height 78vh · radio inferior 48px · overflow hidden
 *   etiqueta  top/left 24px (40px en `sm`) · mono 10px/600 · 0.18em
 *             fondo violet-pop · padding 6/12 · radio 3
 *   bloque    inset-x-0 bottom-0 · padding 24 / 40 / 56
 *             flex · items-end · justify-between
 *   rótulo    mono 11px/600 · 0.22em · blanco 95% (tagline al 70%)
 *   h1        SANS 300 · clamp(26px, 3.8vw, 44px) · 1.1 · -0.02em
 *   precio    SANS 300 · clamp(24px, 3vw, 36px) · tachado mono 13px
 *
 * ── El precio SÍ se repite aquí y en el panel ────────────────────────
 * Es lo que hace el original, y mirándolo se sostiene: arriba es un
 * precio de portada de revista —blanco, sin caja, al filo de la foto— y
 * abajo es el precio OPERATIVO, el que va pegado al selector de cantidad
 * y al botón. No compiten porque no hacen lo mismo: el de arriba informa
 * y el de abajo se usa. La regla de no repetirlo existe para no tener dos
 * precios que puedan discrepar, y aquí no pueden: los dos salen del mismo
 * `priceFor` y los dos pasan por `formatPrice`.
 *
 * ── Sin foto NO se pinta el marcador ─────────────────────────────────
 * `ProductMediaPlaceholder` —el "Próximamente" del catálogo— es un
 * marcador CLARO con su rótulo en `ds-purple`, y bajo el velo cae a
 * 1.7:1. Un aviso ilegible no avisa. En su lugar el fondo de la sección
 * pasa a `ds-grad-violet-deep`, que se lee deliberado y no roto; el
 * original deja ahí su degradado claro con un icono gris, y sobre ese
 * fondo el texto blanco del bloque inferior tampoco llegaría a 4.5:1.
 *
 * ── La etiqueta sale de `badgeLabel`, no de la categoría ─────────────
 * En el original son dos cosas distintas: `editorial.badgeLabel` es la
 * pastilla de arriba a la izquierda y la CATEGORÍA es la primera mitad
 * del rótulo de abajo. Antes aquí la categoría hacía de etiqueta, que
 * dejaba la pastilla puesta en fichas sin editorial y el rótulo inferior
 * vacío. Ningún producto sembrado trae `badgeLabel`: sin él, no hay
 * pastilla, y así es como debe verse.
 */
export function ProductHero({
  image,
  name,
  category,
  tagline,
  badgeLabel,
  price,
  locale,
}: ProductHeroProps) {
  const { t } = useTranslation();
  const { showImage, onError } = useImageFallback(image);
  const numberLocale = numberLocaleOf(locale);
  const discount = discountPercent(price);

  return (
    <section
      aria-label={t("Portada del producto")}
      className="relative w-full overflow-hidden"
      style={{
        minHeight: "78vh",
        borderBottomLeftRadius: 48,
        borderBottomRightRadius: 48,
        backgroundImage: showImage
          ? HERO_BACKDROP
          : "linear-gradient(160deg,#6B5B95 0%,#1F1D2B 100%)",
      }}
    >
      {showImage && (
        <img
          src={image}
          alt={name}
          onError={onError}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}

      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ backgroundImage: HERO_VEIL }}
      />

      {badgeLabel && (
        <p
          className="absolute left-6 top-6 z-10 rounded-[3px] bg-ds-violet-pop px-3 py-1.5 font-mono text-[10px] font-semibold uppercase leading-none tracking-[0.18em] text-white sm:left-10 sm:top-10"
        >
          {badgeLabel}
        </p>
      )}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-end justify-between gap-6 p-6 sm:p-10 lg:p-14">
        <div className="max-w-[65%]">
          {category && (
            <p className="mb-2 font-mono text-[11px] font-semibold uppercase leading-none tracking-[0.22em] text-white/95">
              {category}
              {tagline && <span className="opacity-70"> · {tagline}</span>}
            </p>
          )}

          <h1 className="text-[clamp(26px,3.8vw,44px)] font-light leading-[1.1] tracking-[-0.02em] text-white">
            {name}
          </h1>
        </div>

        {price && (
          <div className="text-right">
            {price.compareAtCents !== undefined && (
              <p className="mb-1 font-mono text-[13px] text-white/70 line-through">
                {formatPrice(price.compareAtCents, price.currency, numberLocale)}
              </p>
            )}

            <p className="text-[clamp(24px,3vw,36px)] font-light leading-none tracking-[-0.02em] text-white">
              {formatPrice(price.cents, price.currency, numberLocale)}
            </p>

            <p className="mt-1 font-mono text-[10px] font-semibold uppercase leading-none tracking-[0.22em] text-white/90">
              {price.currency}
              {discount > 0 ? ` · −${discount}%` : ""}
            </p>

            {/*
             * El aviso de moneda va aquí y no solo abajo: quien eligió USD
             * y ve un precio en pesos tiene que enterarse en el mismo sitio
             * donde lee la cifra, no dos pantallas más abajo.
             */}
            {price.isFallbackCurrency && (
              <p className="mt-1 max-w-[220px] text-[11px] leading-snug text-white/90">
                {t("Precio en {{usada}}", { usada: price.currency })}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
